// ==================== 用户端常量 ====================
const USER_TOKEN_KEY = 'access_token';
const USER_REFRESH_TOKEN_KEY = 'refresh_token';
const USER_TOKEN_EXPIRES_KEY = 'token_expires_at';
const USER_AVATAR_URL_KEY = 'user_avatar_url';

// ==================== 用户端头像函数 ====================

/** 设置用户头像 URL（WebView 模式下从原生 Bridge 获取后存储） */
export function setUserAvatarUrl(url: string): void {
  localStorage.setItem(USER_AVATAR_URL_KEY, url);
}

/** 获取用户头像 URL */
export function getUserAvatarUrl(): string | null {
  return localStorage.getItem(USER_AVATAR_URL_KEY);
}

// ==================== 客服端常量 ====================
const CS_TOKEN_KEY = 'cs_access_token';
const CS_REFRESH_TOKEN_KEY = 'cs_refresh_token';
const CS_USER_KEY = 'cs_user_info';

// ==================== OAuth2 客户端常量 ====================
export const CS_OAUTH2_CLIENT_ID = 'cs-app';
export const CS_OAUTH2_CLIENT_SECRET = 'cs-secret';

// ==================== 类型定义 ====================

/** 客服用户信息 */
export interface CsUserInfo {
  userId: number;
  username: string;
  nickname: string;
  avatar: string;
  token: string;
}

// ==================== JWT 工具函数 ====================

/** Base64URL 解码 */
function base64UrlDecode(str: string): string {
  // Base64URL → Base64：替换 - 为 +，_ 为 /
   let base64 = str.replace(/-/g, '+').replace(/_/g, '/');
  // 补齐 padding
  const pad = base64.length % 4;
  if (pad) {
    base64 += '='.repeat(4 - pad);
  }
  return decodeURIComponent(
    atob(base64)
      .split('')
      .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
      .join('')
  );
}

/** 解码 JWT payload（不验证签名，仅用于前端提取 claims） */
export function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    return JSON.parse(base64UrlDecode(parts[1]));
  } catch {
    return null;
  }
}

/** 从 JWT 中提取客服用户信息 */
export function extractCsUserInfoFromJwt(token: string): CsUserInfo | null {
  const payload = decodeJwtPayload(token);
  if (!payload) return null;

  return {
    userId: Number(payload.userId) || 0,
    username: String(payload.username || ''),
    nickname: String(payload.nickname || ''),
    avatar: String(payload.avatar || ''),
    token,
  };
}

// ==================== 用户端 Token 函数 ====================

/** 获取用户端 Token */
export function getUserToken(): string | null {
  return localStorage.getItem(USER_TOKEN_KEY);
}

/** 设置用户端 Token */
export function setUserToken(token: string): void {
  localStorage.setItem(USER_TOKEN_KEY, token);
}

/** 获取用户端 Refresh Token */
export function getUserRefreshToken(): string | null {
  return localStorage.getItem(USER_REFRESH_TOKEN_KEY);
}

/** 设置用户端 Refresh Token */
export function setUserRefreshToken(token: string): void {
  localStorage.setItem(USER_REFRESH_TOKEN_KEY, token);
}

/** 设置用户端 Token 过期时间 */
export function setUserTokenExpires(expiresIn: number): void {
  const expiresAt = Date.now() + expiresIn * 1000;
  localStorage.setItem(USER_TOKEN_EXPIRES_KEY, String(expiresAt));
}

/** 获取用户端 Token 过期时间戳 */
export function getUserTokenExpires(): number | null {
  const val = localStorage.getItem(USER_TOKEN_EXPIRES_KEY);
  return val ? Number(val) : null;
}

/** 判断用户端是否已登录且 Token 未过期 */
export function isUserAuthenticated(): boolean {
  const token = getUserToken();
  if (!token) return false;

  const expiresAt = getUserTokenExpires();
  if (expiresAt && Date.now() >= expiresAt - 60 * 1000) {
    clearUserAuth();
    return false;
  }

  return true;
}

/** 清除用户端认证信息 */
export function clearUserAuth(): void {
  localStorage.removeItem(USER_TOKEN_KEY);
  localStorage.removeItem(USER_REFRESH_TOKEN_KEY);
  localStorage.removeItem(USER_TOKEN_EXPIRES_KEY);
}

// ==================== 客服端 Token 函数 ====================

/** 获取客服端 Token */
export function getCsToken(): string | null {
  console.log('-------getCsToken', CS_TOKEN_KEY);
  return localStorage.getItem(CS_TOKEN_KEY);
}

/** 设置客服端 Token */
export function setCsToken(token: string): void {
  localStorage.setItem(CS_TOKEN_KEY, token);
}

/** 获取客服端 Refresh Token */
export function getCsRefreshToken(): string | null {
  return localStorage.getItem(CS_REFRESH_TOKEN_KEY);
}

/** 设置客服端 Refresh Token */
export function setCsRefreshToken(token: string): void {
  localStorage.setItem(CS_REFRESH_TOKEN_KEY, token);
}

/** 判断客服端是否已登录 */
export function isCsAuthenticated(): boolean {
  const token = getCsToken();
  return !!token;
}

/** 清除客服端认证信息 */
export function clearCsAuth(): void {
  localStorage.removeItem(CS_TOKEN_KEY);
  localStorage.removeItem(CS_REFRESH_TOKEN_KEY);
  localStorage.removeItem(CS_USER_KEY);
}

// ==================== 客服端用户信息函数 ====================

/** 保存客服用户信息（优先从JWT自动提取） */
export function setCsUserInfo(userInfo: CsUserInfo): void {
  localStorage.setItem(CS_USER_KEY, JSON.stringify(userInfo));
}

/** 设置客服JWT Token并自动提取用户信息存入store */
export function setCsJwtAuth(access_token: string, refresh_token?: string): CsUserInfo {
  setCsToken(access_token);
  if (refresh_token) {
    setCsRefreshToken(refresh_token);
  }
  // 从JWT解码提取用户信息
  const userInfo = extractCsUserInfoFromJwt(access_token);
  if (userInfo) {
    setCsUserInfo(userInfo);
  }
  return userInfo || { userId: 0, username: '', nickname: '', avatar: '', token: access_token };
}

/** 获取客服用户信息 */
export function getCsUserInfo(): CsUserInfo | null {
  const data = localStorage.getItem(CS_USER_KEY);
  if (!data) return null;
  try {
    return JSON.parse(data) as CsUserInfo;
  } catch {
    return null;
  }
}

// ==================== 通用函数 ====================

/** 清除全部认证信息 */
export function clearAllAuth(): void {
  clearUserAuth();
  clearCsAuth();
}
